<!--
  Navigation rail. The active group is marked by a blue left edge and the active page
  by a selected surface, so both levels are readable at a glance. Collapsed, it keeps
  the group edge and shows icons with tooltips.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { ICONS, icon } from '@shiocast/ui';
import { NAVIGATION } from '@/router/navigation';
import { useAuthStore } from '@/stores/auth';

const { t } = useI18n();
const auth = useAuthStore();
const collapsed = ref(false);

/** Only the groups with at least one item the operator may actually open. */
const groups = computed(() =>
  NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.scope || auth.can(item.scope)),
  })).filter((group) => group.items.length > 0),
);

function iconFor(name: string) {
  return icon(name);
}
</script>

<template>
  <nav
    class="shio-rail"
    :class="{ 'shio-rail--collapsed': collapsed }"
    aria-label="Main navigation"
  >
    <!--
      The brand lockup. It is the identity of the product, and it gives an operator a
      fixed point at the top of a long rail. Collapsed, the mark alone carries it.
    -->
    <RouterLink class="shio-rail__brand" to="/" :title="collapsed ? 'ShioCast' : undefined">
      <img class="shio-rail__logo" src="/shiocast-mark.png" alt="" width="36" height="36" />
      <span v-if="!collapsed" class="shio-rail__wordmark">
        <span class="shio-rail__name shio-wordmark">Shio<b>Cast</b></span>
        <span class="shio-rail__tagline">{{ t('nav.tagline') }}</span>
      </span>
    </RouterLink>

    <button
      class="shio-btn shio-btn--ghost shio-btn--icon shio-rail__toggle"
      type="button"
      :aria-expanded="!collapsed"
      :aria-label="collapsed ? t('nav.expand') : t('nav.collapse')"
      @click="collapsed = !collapsed"
    >
      <component :is="collapsed ? ICONS.ChevronRight : ICONS.ChevronLeft" :size="16" />
      <span v-if="!collapsed" class="shio-rail__toggle-label">{{ t('nav.collapse') }}</span>
    </button>

    <div class="shio-rail__scroll">
      <section v-for="group in groups" :key="group.label">
        <h2 v-if="!collapsed" class="shio-nav-group">{{ t(group.label) }}</h2>
        <hr v-else class="shio-rail__divider" />

        <RouterLink
          v-for="item in group.items"
          :key="item.to"
          v-slot="{ href, navigate, isExactActive }"
          :to="item.to"
          custom
        >
          <a
            class="shio-nav-item"
            :href="href"
            :aria-current="isExactActive ? 'page' : undefined"
            :title="collapsed ? t(item.label) : undefined"
            @click="navigate"
          >
            <component :is="iconFor(item.icon)" :size="16" aria-hidden="true" />
            <template v-if="!collapsed">
              <span class="shio-rail__label">{{ t(item.label) }}</span>
            </template>
          </a>
        </RouterLink>
      </section>
    </div>
  </nav>
</template>

<style scoped>
.shio-rail__brand {
  order: 1;
  display: flex;
  align-items: center;
  gap: var(--shio-space-2);
  padding: var(--shio-space-3);
  border-bottom: 1px solid hsl(var(--shio-border));
  text-decoration: none;
}

.shio-rail__logo {
  flex: none;
  width: 36px;
  height: 36px;
  object-fit: contain;
}

.shio-rail__wordmark {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.shio-rail__name {
  font-size: var(--shio-type-card-title-size);
}

.shio-rail__tagline {
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-meta-size);
  color: hsl(var(--shio-text-muted));
  white-space: nowrap;
}

.shio-rail__toggle-label {
  font-family: var(--shio-font-body);
  font-size: var(--shio-type-body-sm-size);
}

.shio-rail {
  width: var(--shio-rail-width);
  background: hsl(var(--shio-bg-panel));
  border-right: var(--shio-border-width) solid hsl(var(--shio-border));
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  transition: width var(--shio-motion-panel) var(--shio-ease);
}

.shio-rail--collapsed {
  width: var(--shio-rail-width-collapsed);
}

/* At the foot of the rail, as V1 had it: the collapse control is used rarely and does
   not deserve a position above the navigation. */
.shio-rail__toggle {
  order: 3;
  align-self: stretch;
  justify-content: flex-start;
  gap: var(--shio-space-2);
  margin: auto var(--shio-space-2) var(--shio-space-2);
  border-top: 1px solid hsl(var(--shio-border));
  border-radius: 0;
  padding-top: var(--shio-space-2);
}

.shio-rail__scroll {
  order: 2;
  flex: 1;
  overflow-y: auto;
  padding-bottom: var(--shio-space-6);
}

.shio-rail__divider {
  border: 0;
  border-top: var(--shio-border-width) solid hsl(var(--shio-border) / 0.6);
  margin: var(--shio-space-2) var(--shio-space-2);
}

.shio-rail__label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.shio-rail--collapsed .shio-nav-item {
  justify-content: center;
}
</style>
